/**
 * Option Entity v1 Schema
 * Single source of truth for exposed entity property fields
 * 
 * This schema defines the locked v1 contract for option entity properties.
 * All fields listed here are user-facing and will appear in Excel's Field menu.
 * 
 * IMPORTANT: This field list is intentionally stable and protected by a test.
 * See docs/entities/OPTION_ENTITY_COMPAT.md for the compatibility policy.
 * 
 * Field ordering is stable (semantic grouping). New fields must be appended
 * at the end of their semantic group. No reordering of existing fields.
 * 
 * Metadata fields (request_id, fetched_at, source, source_note, as_of) are
 * explicitly excluded from this schema and must not appear in entity properties.
 */

export const OPTION_ENTITY_V1_FIELDS = [
  // Core identity (required)
  "underlying",
  "expiration",
  "strike",
  "right",
  "display_name",
  "occ_symbol",

  // Quote (optional)
  "bid",
  "ask",
  "mid",
  "last",
  "change",
  "change_percent",
  "volume",
  "open_interest",

  // Greeks (optional)
  "delta",
  "gamma",
  "theta",
  "vega",
  "rho",
  "iv",
  
  // Timestamp (optional)
  "updatedAt", // NOTE: camelCase is v1 legacy exception (not snake_case)
] as const;

export type OptionEntityV1Field = typeof OPTION_ENTITY_V1_FIELDS[number];

/**
 * Legacy type alias for backward compatibility
 * @deprecated Use OptionEntityV1Field instead
 */
export type OptionEntityField = OptionEntityV1Field;

/**
 * Get the stable list of v1 entity fields
 * @returns Readonly array of v1 field names
 */
export function getOptionEntityV1Fields(): readonly OptionEntityV1Field[] {
  return OPTION_ENTITY_V1_FIELDS;
}

/**
 * Check if a field name is in the v1 schema
 */
export function isV1Field(field: string): field is OptionEntityV1Field {
  return (OPTION_ENTITY_V1_FIELDS as readonly string[]).includes(field);
}
